// client/src/components/messages/ThreadView.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../../axiosInstance';
import { io } from 'socket.io-client';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import FlagIcon from '@mui/icons-material/Flag';
import ReplyIcon from '@mui/icons-material/Reply';
import CloseIcon from '@mui/icons-material/Close';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import AppBarHeader from '../AppBarHeader';
import RichTextEditor from './RichTextEditor';
import MarkdownRenderer from './MarkdownRenderer';
import AttachmentRenderer from './AttachmentRenderer';
import EmbedRenderer from './EmbedRenderer';
import MessageReactions from './MessageReactions';
import PresenceIndicator from './PresenceIndicator';
import TypingIndicator from './TypingIndicator';

const ThreadView = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { isFeatureEnabled } = useFeatureFlags();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;
  const userId = user ? user.id : null;
  const isAdmin = user ? user.isAdmin : false;
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState([]); // Phase 3: Typing indicators
  const [socketStatus, setSocketStatus] = useState('connecting'); // 'connecting', 'connected', 'disconnected'
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!isFeatureEnabled('enableMessageBoardFeature')) {
      navigate('/dashboard');
      return;
    }
    fetchThread();
    fetchMessages();
    markThreadAsRead(); // Mark as read when opening thread (Phase 3)
  }, [threadId, isFeatureEnabled, navigate]);

  // Mark thread as read (Phase 3: Unread Tracking)
  const markThreadAsRead = async () => {
    try {
      await axiosInstance.patch(`/messages/threads/${threadId}/read`);
      console.log('✅ Thread marked as read');
    } catch (err) {
      console.error('Error marking thread as read:', err);
      // Non-critical error, don't show to user
    }
  };

  // Setup Socket.IO connection for real-time updates
  useEffect(() => {
    if (!token || !threadId) return;

    // Connect to Socket.IO through the same origin (goes through nginx proxy)
    
    const socket = io(window.location.origin, {
      path: '/socket.io/',
      auth: { token }
      // Let Socket.IO manage transports automatically
    });

    socketRef.current = socket;

    // Detailed socket event logging
    socket.on('connect', () => {
      console.log(`✅ [${new Date().toLocaleTimeString()}] Socket connected! SID: ${socket.id}`);
      setSocketStatus('connected');
      socket.emit('join-thread', `thread:${threadId}`);
      console.log(`[Socket] Emitting join-thread for thread:${threadId}`);
    });

    socket.on('disconnect', (reason) => {
      console.log(`❌ [${new Date().toLocaleTimeString()}] Socket disconnected. Reason: ${reason}`);
      setSocketStatus('disconnected');
    });

    socket.on('connect_error', (err) => {
      console.error(`🚨 [${new Date().toLocaleTimeString()}] Socket connection error:`, err.message);
      setSocketStatus('disconnected');
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log(`[Socket] Reconnecting... Attempt #${attemptNumber}`);
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`[Socket] Reconnecting... Attempt #${attemptNumber}`);
    });

    socket.on('reconnect_error', (err) => {
      console.error(`[Socket] Reconnection error:`, err.message);
    });

    socket.on('reconnect_failed', () => {
      console.log('[Socket] Reconnection failed. Disconnecting.');
      setSocketStatus('disconnected');
    });

    // Listen for typing indicators from other users
    socket.on('user-typing', ({ userId: typingUserId, username, isTyping }) => {
      console.log(`👀 Received user-typing event: ${username} (${typingUserId}) isTyping=${isTyping}`);
      
      if (isTyping) {
        // Add user to typing list if not already there
        setTypingUsers(prev => {
          if (!prev.find(u => u.userId === typingUserId)) {
            return [...prev, { userId: typingUserId, username }];
          }
          return prev;
        });
      } else {
        // Remove user from typing list
        setTypingUsers(prev => prev.filter(u => u.userId !== typingUserId));
      }
    });

    // Listen for new messages in this thread
    socket.on('new-message', (message) => {
      try {
        const incomingThreadId = message?.threadId;
        if (String(incomingThreadId) !== String(threadId)) return;

        setMessages((prev) => {
          if (!message?._id) return prev;
          if (prev.some((m) => String(m?._id) === String(message._id))) return prev;
          return [...prev, message];
        });

        // If someone sent a message while you're in the thread, mark as read
        markThreadAsRead();
      } catch (err) {
        console.error('Error handling new-message socket event:', err);
      }
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.emit('leave-thread', `thread:${threadId}`);
        console.log(`[Socket] Leaving thread thread:${threadId} and disconnecting.`);
        socket.disconnect();
        setSocketStatus('disconnected');
      }
    };
  }, [threadId, token, userId]);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchThread = async () => {
    try {
      const response = await axiosInstance.get(`/messages/threads/${threadId}`);
      setThread(response.data);
    } catch (err) {
      console.error('Error fetching thread:', err);
      setError(err.response?.data?.error || 'Failed to load thread');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axiosInstance.get(`/messages/threads/${threadId}/messages`);
      setMessages(response.data);
      setError(null);
      // Scroll to bottom after messages load
      setTimeout(() => scrollToBottom(), 100);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(err.response?.data?.error || 'Failed to load messages');
    } finally {
      setLoading(false);
      setTimeout(() => scrollToBottom(), 100);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      
      // Stop typing indicator when sending message (Phase 3: Week 2)
      if (socketRef.current && threadId) {
        socketRef.current.emit('typing-stop', { threadId });
      }
      
      await axiosInstance.post(`/messages/threads/${threadId}/messages`, {
        content: newMessage,
        contentType: 'markdown',
        attachments: attachments,
        replyTo: replyingTo?._id || null
      });
      setNewMessage('');
      setAttachments([]);
      setReplyingTo(null);
      fetchMessages();
    } catch (err) {
      console.error('Error sending message:', err);
      alert(err.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await axiosInstance.delete(`/messages/messages/${messageId}`);
      fetchMessages(); // Refresh messages
    } catch (err) {
      console.error('Error deleting message:', err);
      alert(err.response?.data?.error || 'Failed to delete message');
    }
  };

  const canDeleteMessage = (message) => {
    if (!message || !userId) return false;
    if (isAdmin) return true;
    if (String(message.author) !== String(userId)) return false;
    
    // Check 15-minute window
    const messageAge = Date.now() - new Date(message.createdAt).getTime();
    const fifteenMinutes = 15 * 60 * 1000;
    return messageAge < fifteenMinutes;
  };

  const getTimeRemaining = (message) => {
    if (!message || isAdmin) return null;
    const messageAge = Date.now() - new Date(message.createdAt).getTime();
    const fifteenMinutes = 15 * 60 * 1000;
    const remaining = fifteenMinutes - messageAge;
    if (remaining <= 0) return null;
    const minutes = Math.floor(remaining / 60000);
    return `${minutes}m remaining`;
  };

  const handleArchiveThread = async () => {
    const isCurrentlyArchived = thread?.isArchived;
    const action = isCurrentlyArchived ? 'unarchive' : 'archive';
    const confirmMessage = isCurrentlyArchived 
      ? 'Unarchive this conversation? It will appear in your Active threads.'
      : 'Archive this conversation? It will be hidden from your Active threads.';
    
    if (!window.confirm(confirmMessage)) return;

    try {
      if (isCurrentlyArchived) {
        // Unarchive by setting isArchived to false
        await axiosInstance.patch(`/messages/threads/${threadId}`, { isArchived: false });
      } else {
        // Archive
        await axiosInstance.patch(`/messages/threads/${threadId}/archive`);
      }
      navigate('/messages');
    } catch (err) {
      console.error(`Error ${action}ing thread:`, err);
      alert(err.response?.data?.error || `Failed to ${action} thread`);
    }
  };

  const handleDeleteThread = async () => {
    if (!window.confirm('Permanently delete this conversation? This cannot be undone and will delete all messages.')) return;

    try {
      await axiosInstance.delete(`/messages/threads/${threadId}`);
      navigate('/messages');
    } catch (err) {
      console.error('Error deleting thread:', err);
      alert(err.response?.data?.error || 'Failed to delete thread');
    }
  };

  const handleReportMessage = async (messageId) => {
    const reason = prompt('Why are you reporting this message?\n\nOptions: spam, harassment, inappropriate_content, off_topic, misinformation, other');
    if (!reason) return;
    
    const validReasons = ['spam', 'harassment', 'inappropriate_content', 'off_topic', 'misinformation', 'other'];
    if (!validReasons.includes(reason.toLowerCase())) {
      alert('Invalid reason. Please use one of: spam, harassment, inappropriate_content, off_topic, misinformation, other');
      return;
    }

    try {
      await axiosInstance.post('/reports', {
        targetType: 'message',
        targetId: messageId,
        reason: reason.toLowerCase()
      });
      alert('Report submitted successfully. Our moderation team will review it.');
    } catch (err) {
      console.error('Error reporting message:', err);
      alert(err.response?.data?.error || 'Failed to submit report');
    }
  };

  if (loading) {
    return (
      <>
        <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} hideCollectionNav={true} />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <AppBarHeader isAdmin={isAdmin} userId={userId} onLogout={handleLogout} hideCollectionNav={true} />
      <Box sx={{ maxWidth: 900, margin: '0 auto', p: 2 }}>
      <Button 
        variant="outlined" 
        onClick={() => navigate('/messages')}
        sx={{ mb: 2 }}
      >
        ← Back to Messages
      </Button>

      {thread && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5">
                {thread.title || 'Conversation'}
              </Typography>
              {thread.isLocked && (
                <Chip label="Thread Locked" color="warning" size="small" sx={{ mt: 1 }} />
              )}
              {thread.isArchived && (
                <Chip label="Archived" color="default" size="small" sx={{ mt: 1, ml: 1 }} />
              )}
            </Box>
            <Box>
              {(String(thread.targetUser) === String(userId) || isAdmin) && (
                <>
                  <Tooltip title={thread.isArchived ? "Unarchive conversation" : "Archive conversation"}>
                    <IconButton onClick={handleArchiveThread} size="small">
                      <ArchiveIcon />
                    </IconButton>
                  </Tooltip>
                  {isAdmin && (
                    <Tooltip title="Delete conversation (admin only)">
                      <IconButton onClick={handleDeleteThread} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2, maxHeight: 500, overflowY: 'auto' }}>
        {messages.length === 0 ? (
          <Typography color="text.secondary" align="center">
            No messages yet. Start the conversation!
          </Typography>
        ) : (
          <>
          <List>
            {messages.map((msg, idx) => (
              <React.Fragment key={msg._id}>
                {idx > 0 && <Divider />}
                <ListItem 
                  alignItems="flex-start"
                  secondaryAction={
                    !msg.isDeleted && (
                      <Box>
                        <Tooltip title="Reply">
                          <IconButton 
                            aria-label="reply"
                            onClick={() => setReplyingTo(msg)}
                            size="small"
                          >
                            <ReplyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canDeleteMessage(msg) && (
                          <Tooltip title={getTimeRemaining(msg) || 'Delete message'}>
                            <IconButton 
                              aria-label="delete"
                              onClick={() => handleDeleteMessage(msg._id)}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {String(msg.author?._id || msg.author) !== String(userId) && (
                          <Tooltip title="Report message">
                            <IconButton 
                              aria-label="report"
                              onClick={() => handleReportMessage(msg._id)}
                              size="small"
                            >
                              <FlagIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    )
                  }
                >
                  <ListItemAvatar>
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        src={msg.author?.profileImage ? axiosInstance.getImageUrl(msg.author.profileImage) : null}
                        alt={msg.author?.username || 'User'}
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (msg.author?._id) {
                            navigate(`/collections/${msg.author._id}`);
                          }
                        }}
                      >
                        {msg.author?.username?.[0]?.toUpperCase() || '?'}
                      </Avatar>
                      <PresenceIndicator 
                        lastSeen={msg.author?.lastSeen}
                        isOnline={onlineUsers.has(String(msg.author?._id || msg.author))}
                      />
                    </Box>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box>
                        <Typography 
                          component="span" 
                          variant="subtitle2" 
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                            mr: 1,
                            fontWeight: 'bold'
                          }}
                          onClick={() => {
                            if (msg.author?._id) {
                              navigate(`/collections/${msg.author._id}`);
                            }
                          }}
                        >
                          {msg.author?.username || 'Unknown User'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(msg.createdAt).toLocaleString()}
                          {msg.editedAt && ' (edited)'}
                          {msg.isDeleted && msg.deletedAt && ` • deleted ${new Date(msg.deletedAt).toLocaleString()}`}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      msg.isDeleted ? (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          Message deleted
                        </Typography>
                      ) : (
                        <Box>
                          {msg.replyTo && (
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 1, 
                                mb: 1, 
                                backgroundColor: 'action.hover',
                                borderLeft: '3px solid',
                                borderLeftColor: 'primary.main'
                              }}
                            >
                              <Typography variant="caption" color="text.secondary" display="block">
                                Replying to {msg.replyTo.author?.username || 'Unknown'}
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {msg.replyTo.content}
                              </Typography>
                            </Paper>
                          )}
                          <MarkdownRenderer content={msg.content} contentType={msg.contentType || 'plain'} />
                          <AttachmentRenderer attachments={msg.attachments} />
                          <EmbedRenderer embeds={msg.embeds} />
                          {!msg.isDeleted && (
                            <MessageReactions
                              messageId={msg._id}
                              reactions={msg.reactions || []}
                              currentUserId={userId}
                              onReactionChange={(newReactions) => {
                                setMessages(prev => prev.map(m => 
                                  m._id === msg._id ? { ...m, reactions: newReactions } : m
                                ));
                              }}
                            />
                          )}
                        </Box>
                      )
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
            <div ref={messagesEndRef} />
          </List>
          <TypingIndicator typingUsers={typingUsers} />
          </>
        )}
      </Paper>

      {thread && !thread.isLocked && (
        <Paper sx={{ p: 2 }}>
          {replyingTo && (
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 1, 
                mb: 2, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'action.hover'
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Replying to {replyingTo.author?.username || 'Unknown'}
                </Typography>
                <Typography variant="body2" sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: 400
                }}>
                  {replyingTo.content}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setReplyingTo(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          )}
          <RichTextEditor
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message... Use markdown for formatting!"
            maxLength={2000}
            disabled={sending}
            threadId={threadId}
            attachments={attachments}
            onAttachmentsChange={setAttachments}
            socket={socketStatus === 'connected' ? socketRef.current : null}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button 
              variant="contained" 
              onClick={handleSendMessage}
              disabled={sending || !newMessage.trim()}
            >
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </Box>

          <Typography variant="caption" sx={{ color: 'grey.500', mt: 1, display: 'block' }}>
            Socket Status: {socketStatus}
          </Typography>
        </Paper>
      )}

      {thread && thread.isLocked && (
        <Alert severity="info">
          This thread is locked. Only the owner or an admin can post messages.
        </Alert>
      )}
      </Box>
    </>
  );
};

export default ThreadView;
